import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ChecklistGroupDef, OpenItemRow, UserDto } from '@tww/shared';
import { ROLE_NAMES, fmtDate } from '@tww/shared';
import { api, qs } from '../../api/client';
import { ErrorState, Skeleton } from '../../ui/basics';
import { useSeasons } from './ActionsListPage';
import { DueCell, ROLE_SHORT } from './common';
import { Dropdown } from '../../ui/Dropdown';

/** Offene Punkte über Aktionen (Plan 04 Abschnitt 5.3): Zeitraum, Gewerk, Zuständigkeit; gruppiert nach Gewerk */
export function OpenItemsPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const seasons = useSeasons();
  const p = { from: sp.get('from') ?? '', to: sp.get('to') ?? '', group: sp.get('group') ?? '', role: sp.get('role') ?? '', user: sp.get('user') ?? '', season: sp.get('season') ?? '' };
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };
  const q = useQuery({ queryKey: ['open-items', p], queryFn: () => api.get<{ items: OpenItemRow[]; groups: ChecklistGroupDef[] }>(`/actions/open-items${qs(p)}`), placeholderData: (x) => x });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ items: Pick<UserDto, 'id' | 'display_name' | 'initials'>[] }>('/users/pick').then((r) => r.items), staleTime: 60_000 });
  const groups = q.data?.groups ?? [];
  const items = q.data?.items ?? [];
  const late = items.filter((i) => i.late).length;
  return (
    <>
      <div className="crumb">
        <Link to="/aktionen">Aktionen</Link>
      </div>
      <div className="head">
        <div>
          <h1>Offene Punkte</h1>
          <div className="sub">{q.data ? `${items.length} offene Punkte${late ? `, ${late} überfällig` : ''}` : ''}</div>
        </div>
      </div>
      <div className="filterbar">
        <div className="field">
          <label htmlFor="op-season">Saison</label>
          <Dropdown id="op-season" value={p.season} onChange={(v) => set('season', v)} placeholder="Alle" options={(seasons.data ?? []).map((s) => ({ value: String(s.key), label: s.label }))} />
        </div>
        <div className="field">
          <label htmlFor="op-from">Fällig von</label>
          <input id="op-from" type="date" value={p.from} onChange={(e) => set('from', e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="op-to">bis</label>
          <input id="op-to" type="date" value={p.to} onChange={(e) => set('to', e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="op-group">Gewerk</label>
          <Dropdown id="op-group" value={p.group} onChange={(v) => set('group', v)} placeholder="Alle" options={groups.map((g) => ({ value: String(g.key), label: g.name }))} />
        </div>
        <div className="field">
          <label htmlFor="op-role">Zuständige Rolle</label>
          <Dropdown id="op-role" value={p.role} onChange={(v) => set('role', v)} placeholder="Alle" options={Object.entries(ROLE_NAMES).map(([k, l]) => ({ value: String(k), label: l }))} />
        </div>
        {users.data && (
          <div className="field">
            <label htmlFor="op-user">Person</label>
            <Dropdown id="op-user" value={p.user} onChange={(v) => set('user', v)} placeholder="Alle" options={users.data.map((u) => ({ value: String(u.id), label: u.display_name }))} />
          </div>
        )}
        <div className="spacer" />
        {(p.from || p.to || p.group || p.role || p.user || p.season) && (
          <div className="opts">
            <button type="button" className="btn sm" onClick={() => setSp(new URLSearchParams(), { replace: true })}>
              Zurücksetzen
            </button>
          </div>
        )}
      </div>
      <div className="panel">
        {q.isLoading ? (
          <Skeleton rows={8} />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : items.length === 0 ? (
          <div className="empty">Keine offenen Punkte in dieser Auswahl</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Aktion</th>
                  <th>Punkt</th>
                  <th>Frist</th>
                  <th>Zuständig</th>
                </tr>
              </thead>
              <tbody>
                {groups
                  .filter((g) => items.some((i) => i.group_key === g.key))
                  .map((g) => (
                    <GroupRows key={g.key} group={g} items={items.filter((i) => i.group_key === g.key)} onOpen={(i) => nav(`/aktionen/${i.action_id}?tab=checkliste&punkt=${i.item_id}`)} />
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function GroupRows({ group, items, onOpen }: { group: ChecklistGroupDef; items: OpenItemRow[]; onOpen: (i: OpenItemRow) => void }) {
  return (
    <>
      <tr className="group">
        <td colSpan={4}>
          {group.name} <span className="mut" style={{ fontWeight: 400 }}>· {items.length} offen{items.filter((i) => i.late).length ? `, ${items.filter((i) => i.late).length} überfällig` : ''}</span>
        </td>
      </tr>
      {items.map((i) => (
        <tr key={i.item_id} className="row" onClick={() => onOpen(i)}>
          <td>
            <Link className="link" to={`/aktionen/${i.action_id}`} onClick={(e) => e.stopPropagation()}>
              {i.action_label}
            </Link>
            <span className="sm">{[i.action_code, i.action_date ? fmtDate(i.action_date) : 'Termin offen'].join(' · ')}</span>
          </td>
          <td>{i.title}</td>
          <td>
            <DueCell item={{ state: i.state, effective_due: i.due, late: i.late, optional: false }} />
          </td>
          <td>{i.responsible_user_name ?? (i.responsible_role ? ROLE_SHORT[i.responsible_role] ?? i.responsible_role : '–')}</td>
        </tr>
      ))}
    </>
  );
}
